import { V4HomeResponse } from "~/data/v4_homepage";
import { ClientConfig } from "~/server/types/client";
import { createStore } from "solid-js/store";
import { createResource } from "solid-js";
import {
  HomepageHorizontalSectionBrandCard,
  HomepageMetaData,
  HorizontalSectionResourceMap,
} from "~/server/types/brand";
import { config } from "../config";
import {
  getBrandsByKeys,
  getCategoryCards,
  getPopularBrands,
  getTopNProducts,
} from "../products";
import { getBanners } from "../banners";
import { getStaticJson } from "../static";
import { HorizontalSections } from "~/server/types/search";
import { CategoryCardType } from "../brand_categories";
import { ClassicAppContext } from "./classic_context";
import { sdkEventManager, webEventManager } from "../events";
import { logIntoHubble } from "./logIntoHubble";
import { logIntoPartnerSdkViaPhoneNumber } from "./logIntoPartnerSdkViaPhoneNumber";
import { fetchClientConfig, fetchV4Home } from "../auth";

export function useClassicView(
  classicContextStore: ClassicContextStore
): ClassicAppContext {
  const [store, setStore] =
    createStore<ClassicContextStore>(classicContextStore);
  const homepageMetaDataResource = createResource(
    async (): Promise<HomepageMetaData> => {
      return await _getHomepageMetaData({
        partnerName: store.isLoggedIn
          ? store.clientConfig?.partnerName
          : undefined,
      });
    }
  );

  const isClientDonationType = () => {
    if (!store.isLoggedIn) {
      return false;
    } else {
      if (
        config.donationPartner.includes(
          store.clientConfig?.partnerName ?? "no-client"
        )
      ) {
        return true;
      }
      return false;
    }
  };

  async function _getHomepageMetaData({
    partnerName,
  }: {
    partnerName?: string;
  }): Promise<HomepageMetaData> {
    const topProductsPromise = getTopNProducts(100).then(
      (res) => res.data[0].products
    );
    const popularProductsPromise = getPopularBrands().then(
      (res) => res.data[0].products
    );
    const categoryColumnsPromise: Promise<CategoryCardType[]> =
      getCategoryCards().then((res) => res.data);
    //  store.isLoggedIn
    //   ? getCategoryCards().then((res) => res.data)
    //   : Promise.resolve([]);

    const bannersPromise = (
      partnerName ? getBanners(partnerName) : getBanners()
    ).then((res) => res.banners);

    const horizontalSectionsPromise = getStaticJson(
      config.env === "Prod"
        ? "home_page/home_separators.json"
        : "home_page/home_separators.staging.json"
    ).catch((err) => {
      console.error(err);
      return {
        separators: [],
        brands_text: "",
        upcoming_brands: {
          visible: false,
          color_code: "",
          heading: "",
          brands: [],
        },
      };
    }) as Promise<HorizontalSections>;

    return Promise.all([
      topProductsPromise,
      popularProductsPromise,
      categoryColumnsPromise,
      bannersPromise,
      horizontalSectionsPromise,
    ]).then(function ([
      topProducts,
      popularProducts,
      categoryColumns,
      banners,
      horizontalSections,
    ]): HomepageMetaData {
      const horizontalSectionsResourceMap =
        getResourceMapForHorizontalSections(horizontalSections);
      return {
        topProducts: topProducts,
        popularProducts: popularProducts,
        categoryColumns: categoryColumns,
        banners: banners,
        horizontalSectionsResourceMap: horizontalSectionsResourceMap,
      };
    });

    function getResourceMapForHorizontalSections(
      horizontalSections: HorizontalSections
    ): HorizontalSectionResourceMap[] {
      const sections =
        horizontalSections.separators.map<HorizontalSectionResourceMap>(
          (separator, index) => {
            let heading = separator.heading
              .toLowerCase()
              .includes("new on hubble")
              ? "Newly launched brands"
              : separator.heading;
            return {
              id: separator.id,
              separator: {
                ...separator,
                heading: heading,
              },
              resource: createResource(async () => {
                // await new Promise((resolve) => setTimeout(resolve, 1000 * index));
                const res = await getBrandsByKeys(separator.brands);
                return res.data[0].products.map(
                  (product): HomepageHorizontalSectionBrandCard => {
                    return {
                      id: product.id,
                      category: product.displayTags.at(0) || "",
                      title: product.voucherProduct.title,
                      iconImageUrl: product.voucherProduct.iconImageUrl,
                      rewardType: product.voucherProduct.rewardType,
                      status: product.voucherProduct.status,
                      brandKey: product.brandKey,
                      discountPercentage:
                        product.voucherProduct.discountPercentage,
                    };
                  }
                );
              }),
            };
          }
        );

      const upcomingBrandSection = {
        id: "upcoming_brands",
        separator: {
          id: "upcoming_brands",
          color_code: horizontalSections.upcoming_brands.color_code ?? "",
          heading: horizontalSections.upcoming_brands.heading,
          brands: horizontalSections.upcoming_brands.brands.map(
            (brand) => brand.name
          ),
        },
        resource: createResource(async () => {
          return horizontalSections.upcoming_brands.brands.map(
            (brand): HomepageHorizontalSectionBrandCard => {
              return {
                id: brand.name,
                title: brand.name,
                category: brand.category,
                iconImageUrl: brand.logo_url,
                rewardType: "PREPAID",
                status: "ACTIVE",
                brandKey: "",
                discountPercentage: 0,
              };
            }
          );
        }),
      };

      if (horizontalSections.upcoming_brands.visible ?? true) {
        // show the section by default if no visible flag is provided
        sections.splice(1, 0, upcomingBrandSection);
      }

      return sections;
    }
  }

  function login(onAfterLogin: () => void) {
    async function _afterLogin() {
      setStore("isLoggedIn", true);
      onAfterLogin();

      const v4HomePromise = fetchV4Home();
      const clientConfigPromise = fetchClientConfig();
      const [v4Home, clientConfig] = await Promise.all([
        v4HomePromise,
        clientConfigPromise,
      ]);

      setStore("v4HomePage", v4Home);
      setStore("clientConfig", clientConfig);
    }
    if (store.isLoggedIn) {
      return;
    }

    if (store.mode === "hubble") {
      logIntoHubble(_afterLogin, webEventManager);
    } else if (store.mode === "sdk") {
      logIntoPartnerSdkViaPhoneNumber(_afterLogin, sdkEventManager);
    }
  }

  function logout() {}

  return {
    data: store,
    setClassicContextData: setStore,
    isClientDonationType: isClientDonationType,
    homepageMetaDataResource: homepageMetaDataResource,
    login: login,
    logout: logout,
  };
}

export type LocalClientConfig = {
  showCategories: boolean;
  disableHomePageBackButton: boolean;
  consumeCoinsByDefault: boolean;
};

export type ClassicContextStore = {
  mode: "hubble" | "sdk";
  isLoggedIn: boolean;
  clientConfig: ClientConfig;
  v4HomePage: V4HomeResponse | null;
  clientId: string;
  localClientConfig: LocalClientConfig;
};
